import { InfoOutlined } from "@mui/icons-material";
import { Box, useTheme } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { ContainerCard } from "../../../../components";
import { Tooltip } from "../../../../components/Tooltip";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { useLocalization } from "../../../../hooks/useLocalization";
import type { ExperienceFeedbackRating, FeedbackOverallRating } from "../types";
import { exportFeedbackRatingsCsv } from "../utils/exportCsv";
import {
  FeedbackRatingsChart,
  formatFeedbackCount,
} from "./FeedbackRatingsChart";
import { StarRating } from "./StarRating";

const MIN_FEEDBACK_COUNT = 5;

interface FeedbackRatingsCardProps {
  data: ExperienceFeedbackRating[];
  overallRating: FeedbackOverallRating;
  isLoading?: boolean;
  isFetching?: boolean;
  startDate: Date;
  endDate: Date;
  isUserLevelDataEnabled?: boolean;
}

// ── Overall Rating Header ───────────────────────────────────────────

const OverallRatingDisplay: React.FC<{
  overallRating: FeedbackOverallRating;
  isUserLevelDataEnabled?: boolean;
}> = ({ overallRating, isUserLevelDataEnabled = false }) => {
  const theme = useTheme();
  const { t } = useLocalization();
  const hasSufficientData =
    (isUserLevelDataEnabled ||
      overallRating.totalFeedbackCount >= MIN_FEEDBACK_COUNT) &&
    overallRating.averageRating !== null;

  if (hasSufficientData) {
    return (
      <Box
        component="span"
        sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
      >
        <StarRating rating={overallRating.averageRating} size={24} />
        <Box
          component="span"
          sx={{
            fontWeight: 700,
            fontSize: 18,
            lineHeight: "24px",
            color: theme.colors.text[6],
          }}
        >
          {overallRating.averageRating!.toFixed(1)} (
          {formatFeedbackCount(
            overallRating.totalFeedbackCount,
            isUserLevelDataEnabled,
          )}
          )
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
    >
      <StarRating rating={null} size={24} />
      <Box
        component="span"
        sx={{ fontWeight: 700, fontSize: 16, color: theme.colors.text[0] }}
      >
        N/A (
        {formatFeedbackCount(
          overallRating.totalFeedbackCount,
          isUserLevelDataEnabled,
        )}
        )
      </Box>
      {!isUserLevelDataEnabled && (
        <Tooltip
          message={t(
            "pages.dataAnalytics.internalUsage.feedback.minimumRatings",
          )}
          placement="top"
        >
          <InfoOutlined
            sx={{ fontSize: 18, color: theme.colors.secondary[5] }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

// ── Mobile Card ─────────────────────────────────────────────────────

const FeedbackRatingsMobileCard: React.FC<{
  overallRating: FeedbackOverallRating;
  isFetching?: boolean;
  isUserLevelDataEnabled?: boolean;
}> = ({
  overallRating,
  isFetching = false,
  isUserLevelDataEnabled = false,
}) => {
  const { t } = useLocalization();

  return (
    <ContainerCard
      isFetching={isFetching}
      subtitle={t("pages.dataAnalytics.internalUsage.feedback.mobileSubtitle")}
      title={t("pages.dataAnalytics.internalUsage.feedback.title")}
    >
      <OverallRatingDisplay
        isUserLevelDataEnabled={isUserLevelDataEnabled}
        overallRating={overallRating}
      />
    </ContainerCard>
  );
};

// ── Desktop Card ────────────────────────────────────────────────────

export const FeedbackRatingsCard: React.FC<FeedbackRatingsCardProps> = ({
  data,
  overallRating,
  isFetching = false,
  startDate,
  endDate,
  isUserLevelDataEnabled = false,
}) => {
  const isMobile = useIsMobile();
  const { t } = useLocalization();

  const handleExportCsv = useCallback(() => {
    exportFeedbackRatingsCsv(
      data,
      {
        experienceName: t(
          "pages.dataAnalytics.internalUsage.exportColumns.experienceName",
        ),
        practiceRating: t(
          "pages.dataAnalytics.internalUsage.exportColumns.practiceRating",
        ),
        assessmentRating: t(
          "pages.dataAnalytics.internalUsage.exportColumns.assessmentRating",
        ),
        practiceFeedbackCount: t(
          "pages.dataAnalytics.internalUsage.exportColumns.practiceFeedbackCount",
        ),
        assessmentFeedbackCount: t(
          "pages.dataAnalytics.internalUsage.exportColumns.assessmentFeedbackCount",
        ),
      },
      "feedback-ratings.csv",
      { startDate, endDate },
      {
        startDate: t(
          "pages.dataAnalytics.internalUsage.exportColumns.dateRangeStart",
        ),
        endDate: t(
          "pages.dataAnalytics.internalUsage.exportColumns.dateRangeEnd",
        ),
      },
    );
  }, [data, t, startDate, endDate]);

  const subtitle = useMemo(() => {
    const hasSufficientData =
      overallRating.totalFeedbackCount >= MIN_FEEDBACK_COUNT &&
      overallRating.averageRating !== null;

    if (hasSufficientData) {
      return t("pages.dataAnalytics.internalUsage.feedback.description");
    }

    return t("pages.dataAnalytics.internalUsage.feedback.description");
  }, [overallRating, t]);

  const [sortSlotEl, setSortSlotEl] = useState<HTMLDivElement | null>(null);

  if (isMobile) {
    return (
      <FeedbackRatingsMobileCard
        isFetching={isFetching}
        isUserLevelDataEnabled={isUserLevelDataEnabled}
        overallRating={overallRating}
      />
    );
  }

  const subtitleNode = (
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
    >
      <Box component="span">{subtitle}</Box>
      <OverallRatingDisplay
        isUserLevelDataEnabled={isUserLevelDataEnabled}
        overallRating={overallRating}
      />
    </Box>
  );

  return (
    <ContainerCard
      action={<Box ref={setSortSlotEl} />}
      exportAction={{
        label: t("pages.dataAnalytics.internalUsage.exportCsv"),
        onClick: handleExportCsv,
      }}
      isFetching={isFetching}
      subtitle={subtitleNode}
      title={t("pages.dataAnalytics.internalUsage.feedback.title")}
    >
      <FeedbackRatingsChart
        data={data}
        isUserLevelDataEnabled={isUserLevelDataEnabled}
        sortSlotElement={sortSlotEl}
      />
    </ContainerCard>
  );
};
